import { z } from 'zod';
import { compare, isAnswered, Operator } from './conditions.js';
import { INDUSTRIES } from './controls.js';
import { REVENUE_RANGE } from './intake.js';
/**
 * Insurer proposal forms (task 88, decision 0065). A form is configuration: the insurer's sections and
 * fields, each pointing at where its answer comes from in an assessment, so a client answers once and every
 * insurer's form is filled from the same answers.
 *
 * - A control (how far it is in place, from the latest score). A form question is a statement made to an
 *   insurer under a duty of disclosure, so only a control that is fully in place answers "yes" unless the
 *   mapping says otherwise; a partly-in-place control answers "partial" where the form offers it, else "no".
 * - An answer to a question of the journey, copied, mapped to the form's options, or tested.
 * - The company's details the platform already holds (name, website, contact).
 * - A checklist: each option ticked by its own test.
 * - Fields nobody but the client's director (a declaration) or the broker fills, fields that don't apply,
 *   and fields not yet mapped, which stay on the paper form.
 *
 * Several sources can be listed: the first that gives an answer is used.
 */
export const FORM_FIELD_TYPES = [
    'yes_no', 'choice', 'multi_choice', 'number', 'percentage', 'currency', 'text', 'date', 'table',
]; // prettier-ignore
export const FormFieldType = z.enum(FORM_FIELD_TYPES);
/** The company details the platform holds without asking a question (tenant, client record, intake). */
export const FORM_CONTEXT_KEYS = [
    'company_name', 'website', 'industry', 'contact_name', 'contact_email', 'contact_phone',
]; // prettier-ignore
export const FormContextKey = z.enum(FORM_CONTEXT_KEYS);
const FieldKey = z
    .string()
    .regex(/^[a-z0-9][a-z0-9_]*$/, 'lowercase letters, digits and _')
    .max(64);
const QuestionKey = z.string().min(1).max(64);
const Scalar = z.union([z.string(), z.number(), z.boolean()]);
/** A dotted path into an answer value, e.g. `amount` of a currency answer or `city` of a location. */
const Path = z.string().regex(/^[a-z_]+(\.[a-z_]+)*$/);
const ControlTest = z.strictObject({
    from: z.literal('control'),
    controls: z.array(z.string().min(1)).min(1).max(10),
    /** Several controls: all of them must be in place, or any one. */
    combine: z.enum(['all', 'any']).default('all'),
    /** How far in place counts as "yes": 1 fully (the default), 0.5 at least partly. */
    yes_at: z.union([z.literal(1), z.literal(0.5)]).default(1),
});
const AnswerTest = z.strictObject({
    from: z.literal('answer'),
    question: QuestionKey,
    op: Operator,
    value: z.union([Scalar, z.array(Scalar)]).optional(),
    path: Path.optional(),
});
/** A company detail, such as its industry. */
const ContextTest = z.strictObject({
    from: z.literal('detail'),
    key: FormContextKey,
    op: Operator,
    value: z.union([Scalar, z.array(Scalar)]).optional(),
});
export const FormTest = z.lazy(() => z.union([
    ControlTest,
    AnswerTest,
    ContextTest,
    z.strictObject({ from: z.enum(['all', 'any']), of: z.array(FormTest).min(1) }),
    z.strictObject({ from: z.literal('not'), of: FormTest }),
]));
const ValueSource = z.strictObject({
    from: z.literal('value'),
    question: QuestionKey,
    path: Path.optional(),
    /** Answer value → form value. A value it doesn't list gives no answer (the next source is tried). */
    map: z.record(z.string(), Scalar).optional(),
});
const ContextSource = z.strictObject({ from: z.literal('context'), key: FormContextKey });
const ChecklistSource = z.strictObject({ from: z.literal('checklist') });
const ManualSource = z.strictObject({
    from: z.enum(['declaration', 'broker', 'not_applicable', 'unmapped']),
    note: z.string().max(300).optional(),
});
export const FormFieldSource = z.union([ControlTest, AnswerTest, ContextTest, z.strictObject({ from: z.enum(['all', 'any']), of: z.array(FormTest).min(1) }), z.strictObject({ from: z.literal('not'), of: FormTest }), ValueSource, ContextSource, ChecklistSource, ManualSource]); // prettier-ignore
export const BLOCK_EFFECTS = ['refer', 'decline', 'terms'];
export const FormOption = z.strictObject({
    value: FieldKey,
    label: z.string().min(1).max(200),
    /** A checklist option is ticked when this holds. */
    test: FormTest.optional(),
});
export const FormField = z
    .strictObject({
    key: FieldKey,
    /** The insurer's own numbering, so the broker can find the field on the paper form. */
    ref: z.string().min(1).max(40),
    label: z.string().min(1).max(300),
    type: FormFieldType,
    options: z.array(FormOption).default([]),
    source: z.union([FormFieldSource, z.array(FormFieldSource).min(1).max(5)]),
    required: z.boolean().default(true),
    /**
     * Only asked when another field of the form has one of these values ("If yes, ..."). For a checklist,
     * when any of these boxes is ticked.
     */
    if: z.strictObject({ field: FieldKey, in: z.array(Scalar).min(1) }).optional(),
    /** Doesn't apply to this business when this holds, e.g. no subsidiaries for a group question. */
    na_when: FormTest.optional(),
    /**
     * An answer that stops or changes a quote (task 91): one of `in`, or for a checklist none of `lacks`
     * ticked. "refer": to the insurer's underwriters; "decline": no quote; "terms": likely narrower cover.
     */
    blocks: z
        .strictObject({
        in: z.array(Scalar).min(1).optional(),
        lacks: z.array(Scalar).min(1).optional(),
        effect: z.enum(BLOCK_EFFECTS),
        note: z.string().min(1).max(300),
    })
        .refine((b) => !b.in !== !b.lacks, 'give in or lacks')
        .optional(),
})
    .superRefine((f, ctx) => {
    const sources = Array.isArray(f.source) ? f.source : [f.source];
    const issue = (message, path = []) => ctx.addIssue({ code: 'custom', message, path }); // prettier-ignore
    if (['choice', 'multi_choice', 'yes_no'].includes(f.type) !== f.options.length > 0)
        issue(f.options.length ? `a ${f.type} field has no options` : `a ${f.type} field needs options`, ['options']); // prettier-ignore
    if (f.type === 'yes_no' &&
        !(f.options.some((o) => o.value === 'yes') && f.options.some((o) => o.value === 'no')))
        issue('a yes_no field needs the options yes and no', ['options']);
    const checklist = sources.some((s) => s.from === 'checklist');
    if (checklist && f.type !== 'multi_choice')
        issue('only a multi_choice field is a checklist', ['source']);
    if (checklist && !f.options.some((o) => o.test))
        issue('a checklist needs options with tests', ['options']);
    if (!checklist && f.options.some((o) => o.test))
        issue('option tests only count in a checklist', ['options']);
    const tests = sources.filter((s) => ['control', 'answer', 'detail', 'all', 'any', 'not'].includes(s.from));
    if (tests.length && f.type !== 'yes_no')
        issue('a test answers only a yes_no field', ['source']);
    const keys = f.options.map((o) => o.value);
    if (new Set(keys).size !== keys.length)
        issue('an option is listed twice', ['options']);
    if (f.blocks?.lacks && f.type !== 'multi_choice')
        issue('only a checklist blocks on what it lacks', ['blocks']);
    const blocking = [...(f.blocks?.in ?? []), ...(f.blocks?.lacks ?? [])];
    if (keys.length && blocking.some((v) => !keys.includes(String(v))))
        issue(`blocks on a value the field doesn't have: ${blocking.filter((v) => !keys.includes(String(v))).join(', ')}`, ['blocks']); // prettier-ignore
});
/**
 * The insurer's own premium table, where its form prints one (task 91): a premium for each limit in each
 * revenue band, before taxes and stamp duty.
 */
export const FormRating = z
    .strictObject({
    /** Each revenue band's upper end in RM, inclusive and rising; the first band starts at 0. */
    bands: z.array(z.number().positive()).min(1).max(20),
    rows: z
        .array(z.strictObject({
        limit: z.number().positive(),
        deductible: z.number().min(0),
        premium: z.array(z.number().min(0)),
        /** With an optional extension: the premium in each band, instead of `premium`. */
        with: z.record(z.string().regex(/^[a-z][a-z0-9_]*$/), z.array(z.number().min(0))).default({}),
    }))
        .min(1)
        .max(20),
    extensions: z.record(z.string().regex(/^[a-z][a-z0-9_]*$/), z.string().min(1).max(200)).default({}),
    note: z.string().max(300).default(''),
})
    .superRefine((r, ctx) => {
    const issue = (message) => ctx.addIssue({ code: 'custom', message });
    if (r.bands.some((b, i) => i > 0 && b <= r.bands[i - 1]))
        issue('revenue bands must rise');
    for (const row of r.rows) {
        if (row.premium.length !== r.bands.length)
            issue(`the RM${row.limit} row needs a premium for each band`);
        for (const [k, ps] of Object.entries(row.with)) {
            if (!(k in r.extensions))
                issue(`the RM${row.limit} row prices an unknown extension ${k}`);
            if (ps.length !== r.bands.length)
                issue(`the RM${row.limit} row with ${k} needs a premium for each band`);
        }
    }
});
/**
 * Where a field goes on the insurer's own fillable PDF (task 92, decision 0069). Widget names are the
 * PDF's AcroForm field names, which are generic ("Text Field 3", "b1 Group2"), so each is mapped by hand
 * against the blank form.
 */
const Widget = z.string().min(1).max(120);
export const PdfTarget = z.union([
    /** A text box. */
    Widget,
    /** A long text written across several boxes, a line in each. */
    z.strictObject({ lines: z.array(Widget).min(2).max(8) }),
    /** A radio group: the export state for each value, e.g. { yes: '/0', no: '/1' }. */
    z.strictObject({ radio: Widget, states: z.record(z.string(), z.string().min(1)) }),
    /**
     * One checkbox per value, ticked with its own on-state: a checklist's boxes, a Yes and a No box, or a
     * limit's amount. `tick` is ticked whenever the field is filled; a value no box takes goes in `other`.
     */
    z.strictObject({ boxes: z.record(z.string(), Widget), tick: Widget.optional(), other: Widget.optional() }),
    /**
     * A table's rows: by position ("0", "1"…), or by the value of column `by`. Each row maps columns to text
     * boxes; `_tick` is a box ticked when the row is there.
     */
    z.strictObject({ table: z.record(z.string(), z.record(z.string(), Widget)), by: z.string().optional() }),
    /** A table written a column to a box, its rows joined: "what happened" in one box, "the cost" in another. */
    z.strictObject({ columns: z.record(z.string(), Widget) }),
]);
/** Every widget a target writes to. */
function pdfWidgets(t) {
    if (typeof t === 'string')
        return [t];
    if ('lines' in t)
        return t.lines;
    if ('radio' in t)
        return [t.radio];
    if ('boxes' in t)
        return [...Object.values(t.boxes), ...(t.tick ? [t.tick] : []), ...(t.other ? [t.other] : [])];
    if ('columns' in t)
        return Object.values(t.columns);
    return Object.values(t.table).flatMap((row) => Object.values(row));
}
export const FormPdf = z.strictObject({
    /** The blank form the widgets were mapped on: its SHA-256, so a different edition is refused. */
    sha256: z.string().regex(/^[0-9a-f]{64}$/),
    pages: z.number().int().min(1).max(100),
    fields: z.record(z.string(), PdfTarget),
    /**
     * A premium table printed as a grid of boxes (QBE): the radio group, and its export state for each cell
     * as `extension:limit:band`, with `main` for the cover without an extension and the band's index.
     */
    quote: z.strictObject({ radio: Widget, states: z.record(z.string(), z.string().min(1)) }).optional(),
});
export const FormSection = z.strictObject({
    key: FieldKey,
    title: z.string().min(1).max(200),
    /** A supplement the client completes only when it applies (OT, retail, biometrics...). */
    applies: z.array(FormTest).max(5).optional(),
    fields: z.array(FormField).min(1).max(200),
});
/**
 * Who a form is for (task 90, decision 0067). A client outside it is placed on another form, or referred to
 * the insurer's underwriters instead of bound on this one.
 */
export const FormEligibility = z
    .strictObject({
    /** Annual revenue in RM: at least `min`, and at most `max` or under `below`. */
    revenue: z
        .strictObject({
        min: z.number().min(0).optional(),
        max: z.number().positive().optional(),
        below: z.number().positive().optional(),
    })
        .refine((r) => r.max === undefined || r.below === undefined, 'give max or below, not both')
        .optional(),
    /** Yes/no fields of the form that must be "Yes", such as QBE's excluded industries. */
    fields: z.array(FieldKey).max(30).default([]),
})
    .refine((e) => e.revenue || e.fields.length, 'an eligibility needs revenue or fields');
export const InsurerFormDefinition = z
    .strictObject({
    insurer: z.string().min(1).max(120),
    product: z.string().min(1).max(200),
    /** The insurer's form number and edition, e.g. CDSAB006-PF-0924. */
    form_code: z.string().min(1).max(80),
    country: z.string().length(2).default('MY'),
    description: z.string().max(1000).default(''),
    /** The blank form the fields are numbered against (a file name, kept with the broker's documents). */
    source_document: z.string().max(300).optional(),
    sections: z.array(FormSection).min(1).max(60),
    eligibility: FormEligibility.optional(),
    rating: FormRating.optional(),
    pdf: FormPdf.optional(),
})
    .superRefine((form, ctx) => {
    const seen = new Map();
    form.sections.forEach((s, i) => s.fields.forEach((f, j) => {
        if (seen.has(f.key))
            ctx.addIssue({ code: 'custom', message: `field ${f.key} is in both ${seen.get(f.key)} and ${s.key}`, path: ['sections', i, 'fields', j, 'key'] }); // prettier-ignore
        seen.set(f.key, s.key);
    }));
    const fields = new Map(form.sections.flatMap((s) => s.fields.map((f) => [f.key, f])));
    form.sections.forEach((s, i) => s.fields.forEach((f, j) => {
        if (!f.if)
            return;
        const parent = fields.get(f.if.field);
        const path = ['sections', i, 'fields', j, 'if'];
        if (!parent)
            ctx.addIssue({ code: 'custom', message: `${f.key} depends on unknown field ${f.if.field}`, path }); // prettier-ignore
        else if (parent.options.length && f.if.in.some((v) => !parent.options.some((o) => o.value === v)))
            ctx.addIssue({
                code: 'custom',
                message: `${f.if.field} has no option ${f.if.in.join(' or ')}`,
                path,
            });
    }));
    form.eligibility?.fields.forEach((key, i) => {
        const f = fields.get(key);
        const sources = f ? (Array.isArray(f.source) ? f.source : [f.source]) : [];
        if (!f || f.type !== 'yes_no' || sources.some((s) => MANUAL[s.from]))
            ctx.addIssue({ code: 'custom', message: `eligibility reads ${key}, which is not a yes/no field the assessment answers`, path: ['eligibility', 'fields', i] }); // prettier-ignore
    });
    const used = new Map();
    for (const [key, t] of Object.entries(form.pdf?.fields ?? {})) {
        const f = fields.get(key);
        const path = ['pdf', 'fields', key];
        for (const w of pdfWidgets(t)) {
            if (used.has(w))
                ctx.addIssue({ code: 'custom', message: `${key} and ${used.get(w)} both use the PDF's ${w}`, path }); // prettier-ignore
            used.set(w, key);
        }
        if (!f)
            ctx.addIssue({ code: 'custom', message: `the PDF maps unknown field ${key}`, path });
        else if (typeof t === 'object' && 'boxes' in t && f.options.length && Object.keys(t.boxes).some((v) => !f.options.some((o) => o.value === v))) // prettier-ignore
            ctx.addIssue({ code: 'custom', message: `the PDF boxes for ${key} name a value it doesn't have`, path });
        else if (typeof t === 'object' && 'radio' in t && f.options.some((o) => !(o.value in t.states)))
            ctx.addIssue({ code: 'custom', message: `the PDF radio for ${key} has no state for ${f.options.filter((o) => !(o.value in t.states)).map((o) => o.value).join(', ')}`, path }); // prettier-ignore
    }
});
// ---------- references, for validation against the rest of the configuration ----------
/** The controls and questions a section's fields read (not counting its `applies`). */
function sectionReferences(s, controls = new Set(), questions = new Set()) {
    const visit = referenceVisitor(controls, questions);
    for (const f of s.fields) {
        (Array.isArray(f.source) ? f.source : [f.source]).forEach(visit);
        if (f.na_when)
            visit(f.na_when);
        f.options.forEach((o) => o.test && visit(o.test));
    }
    return { controls, questions };
}
const referenceVisitor = (controls, questions) => {
    const visit = (t) => {
        if (t.from === 'control')
            t.controls.forEach((c) => controls.add(c));
        else if (t.from === 'answer' || t.from === 'value')
            questions.add(t.question);
        else if (t.from === 'all' || t.from === 'any')
            t.of.forEach(visit);
        else if (t.from === 'not')
            visit(t.of);
    };
    return visit;
};
/** The controls and questions a form reads, so publishing can check that they exist. */
export function formReferences(form) {
    const controls = new Set();
    const questions = new Set();
    for (const s of form.sections) {
        s.applies?.forEach(referenceVisitor(controls, questions));
        sectionReferences(s, controls, questions);
    }
    return { controls, questions };
}
/**
 * The comparisons a form makes on answers, so publishing can check option values: each answer test, and
 * each value map as the answer values it lists.
 */
export function formAnswerTests(form) {
    const out = [];
    const visit = (t) => {
        if (t.from === 'answer' && !t.path)
            out.push({ question: t.question, op: t.op, value: t.value });
        else if (t.from === 'value' && t.map && !t.path)
            out.push({ question: t.question, op: 'in', value: Object.keys(t.map) }); // prettier-ignore
        else if (t.from === 'all' || t.from === 'any')
            t.of.forEach(visit);
        else if (t.from === 'not')
            visit(t.of);
    };
    for (const s of form.sections) {
        s.applies?.forEach(visit);
        for (const f of s.fields) {
            (Array.isArray(f.source) ? f.source : [f.source]).forEach(visit);
            if (f.na_when)
                visit(f.na_when);
            f.options.forEach((o) => o.test && visit(o.test));
        }
    }
    return out;
}
// ---------- asking what the chosen forms need (task 89) ----------
/** The question that says which insurers' forms an assessment prepares. Its options are the forms' keys. */
export const FORMS_QUESTION = 'UW-FORMS';
/** A form test as a display condition, or null when it can't be one (it reads a control's score). */
export function formTestCondition(t) {
    const value = (v) => (v === undefined ? {} : { value: v });
    switch (t.from) {
        case 'answer':
            return t.path ? null : { question: t.question, op: t.op, ...value(t.value) };
        case 'detail':
            return { fact: t.key, op: t.op, ...value(t.value) };
        case 'control':
            return null;
        case 'all':
        case 'any': {
            const cs = t.of.map(formTestCondition);
            if (cs.includes(null))
                return null;
            return t.from === 'all' ? { all: cs } : { any: cs };
        }
        case 'not': {
            const c = formTestCondition(t.of);
            return c ? { not: c } : null;
        }
    }
}
/**
 * When each question the forms read is needed: once the assessment prepares a form that reads it (the
 * forms question includes the form's key) and that part of the form applies to the business. A section
 * that applies only to some businesses (retail, OT, biometrics) adds its condition; one that can't be a
 * condition (it reads a score) is left out, so the question is asked rather than missed.
 */
export function formQuestionNeeds(forms) {
    const needs = new Map();
    const need = (q, when) => {
        const m = needs.get(q) ?? new Map();
        m.set(JSON.stringify(when), when);
        needs.set(q, m);
    };
    for (const [key, form] of Object.entries(forms)) {
        const chosen = { question: FORMS_QUESTION, op: 'contains', value: key };
        for (const s of form.sections) {
            const appliesReads = new Set();
            s.applies?.forEach(referenceVisitor(new Set(), appliesReads));
            for (const q of appliesReads)
                need(q, chosen);
            const applies = (s.applies ?? []).map(formTestCondition).filter((c) => c !== null);
            const when = applies.length ? { all: [chosen, ...applies] } : chosen;
            for (const q of sectionReferences(s).questions)
                need(q, when);
        }
    }
    return new Map([...needs].map(([q, m]) => {
        const cs = [...m.values()];
        // A question needed unconditionally by a form doesn't also need that form's narrower conditions.
        const plain = new Set(cs.filter((c) => 'question' in c && c.question === FORMS_QUESTION).map((c) => JSON.stringify(c))); // prettier-ignore
        const kept = cs.filter((c) => !('all' in c && plain.has(JSON.stringify(c.all[0]))));
        return [q, kept.length === 1 ? kept[0] : { any: kept }];
    }));
}
/** Never true: a form question no form reads is not asked. */
const NEVER = { all: [{ question: FORMS_QUESTION, op: 'answered' }, { question: FORMS_QUESTION, op: 'not_answered' }] }; // prettier-ignore
/**
 * Fits the questions to the bundle's insurer forms (task 96), as a bundle is built: the forms question
 * offers each form there is (keeping its labels, in its order, a new form last), and each question asked
 * for the forms (`for_forms`) is shown when a chosen form reads it, on top of its own visibility. A form
 * changed or added in the studio changes the questions with the release that publishes it.
 */
export function compileForms(items) {
    // Published bodies are already parsed; a library or test bundle may carry them as written.
    const forms = {};
    for (const [k, b] of Object.entries(items.insurer_form ?? {})) {
        const r = InsurerFormDefinition.safeParse(b);
        if (r.success)
            forms[k] = r.data;
    }
    const fq = items.question[FORMS_QUESTION]; // prettier-ignore
    const asked = Object.entries(items.question).filter(([, q]) => q.for_forms);
    if (!fq && !asked.length)
        return items;
    const question = { ...items.question };
    if (fq) {
        const listed = new Map((fq.options ?? []).map((o) => [o.value, o]));
        const keys = [...listed.keys()].filter((k) => k in forms);
        for (const k of Object.keys(forms).sort())
            if (!listed.has(k))
                keys.push(k);
        question[FORMS_QUESTION] = {
            ...fq,
            options: keys.map((k) => listed.get(k) ?? { value: k, label: `${forms[k].insurer}: ${forms[k].product}`, effects: [] }), // prettier-ignore
        };
    }
    const needs = formQuestionNeeds(forms);
    for (const [key, q] of asked) {
        const own = q.visibility;
        const need = needs.get(key) ?? NEVER;
        question[key] = { ...q, visibility: own ? { all: [need, own] } : need };
    }
    return { ...items, question };
}
/** A score's control results as a form reads them: how far each control is in place. */
export const controlsFromScore = (scores) => Object.fromEntries(scores.map((c) => [c.control, c.implementation]));
/** How a field stands once the form is filled. */
export const FIELD_STATUSES = [
    'filled', 'missing', 'not_asked', 'declaration', 'broker', 'not_applicable', 'unmapped',
]; // prettier-ignore
const at = (v, path) => path ? path.split('.').reduce((o, k) => (o && typeof o === 'object' ? o[k] : undefined), v) : v; // prettier-ignore
function implementation(t, input) {
    const vs = t.controls.map((c) => input.controls[c] ?? null);
    if (t.combine === 'all') {
        if (vs.some((v) => v === 0))
            return 0;
        return vs.some((v) => v === null) ? null : Math.min(...vs);
    }
    if (vs.some((v) => v === 1))
        return 1;
    return vs.some((v) => v === null) ? null : Math.max(...vs);
}
/** A test's outcome: true, false, or null when the answers don't say. */
export function evaluateFormTest(t, input) {
    switch (t.from) {
        case 'control': {
            const v = implementation(t, input);
            return v === null ? null : v >= t.yes_at;
        }
        case 'answer': {
            const v = at(input.answers[t.question], t.path);
            if (t.op === 'answered' || t.op === 'not_answered')
                return compare(v, t.op, t.value);
            return isAnswered(v) ? compare(v, t.op, t.value) : null;
        }
        case 'detail': {
            const v = input.context[t.key];
            return isAnswered(v) ? compare(v, t.op, t.value) : null;
        }
        case 'all': {
            const vs = t.of.map((x) => evaluateFormTest(x, input));
            return vs.includes(false) ? false : vs.includes(null) ? null : true;
        }
        case 'any': {
            const vs = t.of.map((x) => evaluateFormTest(x, input));
            return vs.includes(true) ? true : vs.includes(null) ? null : false;
        }
        case 'not': {
            const v = evaluateFormTest(t.of, input);
            return v === null ? null : !v;
        }
    }
}
const describe = (s) => {
    switch (s.from) {
        case 'control':
            return `control:${s.controls.join('+')}`;
        case 'answer':
        case 'value':
            return `answer:${s.question}`;
        case 'context':
            return `context:${s.key}`;
        default:
            return s.from;
    }
};
/** The boxes a checklist ticks, and the ones the answers don't settle. */
function checklist(f, input) {
    const ticks = f.options.filter((o) => o.test).map((o) => [o.value, evaluateFormTest(o.test, input)]); // prettier-ignore
    const ticked = ticks.filter(([, v]) => v).map(([k]) => k);
    const unknown = ticks.filter(([, v]) => v === null).map(([k]) => k);
    // "None of the above" (an option without a test) is ticked when every other box is known and empty.
    if (!ticked.length && !unknown.length && f.options.some((o) => o.value === 'none' && !o.test))
        ticked.push('none');
    return { ticked, unknown };
}
/** One source's value for a field, or undefined when it gives none. */
function resolve(f, s, input) {
    switch (s.from) {
        case 'control': {
            const v = implementation(s, input);
            if (v === null)
                return undefined;
            if (v >= s.yes_at)
                return 'yes';
            // Partly in place: the form's "partial" where it has one; otherwise not a "yes" (duty of disclosure).
            return v > 0 && f.options.some((o) => o.value === 'partial') ? 'partial' : 'no';
        }
        case 'answer':
        case 'detail':
        case 'all':
        case 'any':
        case 'not': {
            const v = evaluateFormTest(s, input);
            return v === null ? undefined : v ? 'yes' : 'no';
        }
        case 'value': {
            const v = at(input.answers[s.question], s.path);
            if (!isAnswered(v))
                return undefined;
            if (!s.map)
                return v;
            if (Array.isArray(v)) {
                const mapped = v.map((x) => s.map[String(x)]).filter((x) => x !== undefined);
                return mapped.length ? [...new Set(mapped)] : undefined;
            }
            return s.map[String(v)];
        }
        case 'context': {
            const v = input.context[s.key];
            if (!isAnswered(v))
                return undefined;
            // The industry is held as a pack key; the form shows its name.
            return s.key === 'industry' ? (INDUSTRIES[v] ?? v) : v;
        }
        case 'checklist': {
            // A checklist is filled once every box is known: an unknown box can't be left blank honestly.
            const { ticked, unknown } = checklist(f, input);
            return unknown.length ? undefined : ticked;
        }
        default:
            return undefined;
    }
}
const MANUAL = {
    declaration: 'declaration',
    broker: 'broker',
    not_applicable: 'not_applicable',
    unmapped: 'unmapped',
};
/** Fills a form from an assessment's answers and controls. Pure: the same input gives the same form. */
export function fillInsurerForm(form, input) {
    const values = new Map();
    const fields = [];
    for (const section of form.sections) {
        const applies = section.applies?.map((t) => evaluateFormTest(t, input)) ?? [];
        const sectionOff = applies.length > 0 && applies.every((v) => v === false);
        for (const f of section.fields) {
            const base = { key: f.key, ref: f.ref, section: section.key, label: f.label, type: f.type, required: f.required }; // prettier-ignore
            const sources = Array.isArray(f.source) ? f.source : [f.source];
            const manual = sources.length === 1 ? MANUAL[sources[0].from] : undefined;
            const parent = f.if ? values.get(f.if.field) : undefined;
            const parentOff = f.if && parent !== undefined && !(Array.isArray(parent) ? parent : [parent]).some((v) => f.if.in.includes(v)); // prettier-ignore
            let status;
            let value = null;
            let basis = null;
            let unknown;
            if (sectionOff || parentOff)
                status = 'not_asked';
            else if (manual)
                status = manual;
            else if (f.na_when && evaluateFormTest(f.na_when, input) === true)
                status = 'not_applicable';
            else {
                status = 'missing';
                for (const s of sources) {
                    const v = resolve(f, s, input);
                    if (v === undefined)
                        continue;
                    [status, value, basis] = ['filled', v, describe(s)];
                    break;
                }
                if (status === 'missing' && sources.some((s) => s.from === 'checklist')) {
                    const c = checklist(f, input);
                    [value, unknown] = [c.ticked, c.unknown];
                }
            }
            if (status === 'filled')
                values.set(f.key, value);
            fields.push({ ...base, status, value, basis, ...(unknown ? { unknown } : {}) });
        }
    }
    const summary = Object.fromEntries(FIELD_STATUSES.map((s) => [s, 0]));
    for (const f of fields)
        summary[f.status]++;
    const required = fields.filter((f) => f.required && (f.status === 'filled' || f.status === 'missing'));
    summary.answerable = summary.filled + summary.missing;
    summary.completeness = required.length
        ? Math.round((required.filter((f) => f.status === 'filled').length / required.length) * 1000) / 1000
        : 1;
    return { fields, summary };
}
/** How a form's fields are sourced, before any answers: how much of it the assessment can fill. */
export function formCoverage(form) {
    const by = {
        assessment: 0, declaration: 0, broker: 0, not_applicable: 0, unmapped: 0,
    }; // prettier-ignore
    for (const s of form.sections)
        for (const f of s.fields) {
            const sources = Array.isArray(f.source) ? f.source : [f.source];
            const manual = sources.length === 1 ? MANUAL[sources[0].from] : undefined;
            by[manual ?? 'assessment']++;
        }
    const total = Object.values(by).reduce((a, b) => a + b, 0);
    return { total, ...by, share: total ? Math.round((by.assessment / total) * 1000) / 1000 : 0 };
}
// ---------- eligibility: which forms a client goes on (task 90) ----------
/** The questions that give annual revenue in RM, most specific first. */
export const REVENUE_QUESTIONS = ['UW-REVENUE-CURRENT', 'Q301'];
export const ELIGIBILITY_STATUSES = ['eligible', 'to_confirm', 'ineligible'];
const rm = (n) => `RM${n.toLocaleString('en-US')}`;
const revenueLabel = (r) => {
    const top = r.max !== undefined ? `up to ${rm(r.max)}` : r.below !== undefined ? `below ${rm(r.below)}` : null;
    if (r.min !== undefined && top)
        return `Annual revenue from ${rm(r.min)}, ${top}`;
    return `Annual revenue ${top ?? `of ${rm(r.min ?? 0)} or more`}`;
};
/** Annual revenue in RM from the answers, and the question that gave it; null before it is answered. */
export function answeredRevenue(answers) {
    for (const q of REVENUE_QUESTIONS) {
        const a = answers[q];
        // A currency answer is { amount, currency }.
        const v = a && typeof a === 'object' && 'amount' in a ? a.amount : a;
        if (typeof v === 'number')
            return { rm: v, question: q };
    }
    return null;
}
function revenueCheck(r, input) {
    const min = r.min ?? 0;
    const fits = (x) => x >= min && (r.max === undefined || x <= r.max) && (r.below === undefined || x < r.below); // prettier-ignore
    const known = answeredRevenue(input.answers);
    if (known)
        return { result: fits(known.rm) ? 'pass' : 'fail', basis: `answer:${known.question}`, likely: true }; // prettier-ignore
    if (!input.revenue_range)
        return { result: 'unknown', basis: null, likely: true };
    const [lo, hi] = input.revenue_range;
    const top = r.max ?? r.below ?? Infinity;
    const within = lo >= min && hi !== null && hi <= top;
    const apart = (hi !== null && hi <= min) || (r.max !== undefined ? lo > r.max : r.below !== undefined && lo >= r.below); // prettier-ignore
    return { result: within ? 'pass' : apart ? 'fail' : 'unknown', basis: 'intake:revenue', likely: fits(lo) }; // prettier-ignore
}
/** Whether a client fits a form, from its answers so far or only its intake form. */
export function formEligibility(form, input) {
    const e = form.eligibility;
    const checks = [];
    let likely = true;
    if (e?.revenue) {
        const c = revenueCheck(e.revenue, input);
        likely = c.likely;
        checks.push({ key: 'revenue', label: revenueLabel(e.revenue), result: c.result, basis: c.basis });
    }
    const fields = new Map(form.sections.flatMap((s) => s.fields.map((f) => [f.key, f])));
    for (const key of e?.fields ?? []) {
        const f = fields.get(key);
        let check = { key, label: f.label, result: 'unknown', basis: null };
        for (const s of Array.isArray(f.source) ? f.source : [f.source]) {
            const v = resolve(f, s, input);
            if (v === undefined)
                continue;
            check = { ...check, result: v === 'yes' ? 'pass' : 'fail', basis: describe(s) };
            break;
        }
        checks.push(check);
    }
    const status = checks.some((c) => c.result === 'fail')
        ? 'ineligible'
        : checks.some((c) => c.result === 'unknown')
            ? 'to_confirm'
            : 'eligible';
    return { status, checks, proposed: status !== 'ineligible' && likely };
}
/** The forms to propose for a client, by key. */
export const proposeForms = (forms, input) => Object.keys(forms)
    .sort()
    .filter((k) => formEligibility(forms[k], input).proposed);
/**
 * What a client's intake form says that the journey asks: carried into its assessment when the submission
 * is accepted, so the client isn't asked twice.
 */
export function intakeFacts(t) {
    const out = {};
    if (t.revenue_amount !== undefined)
        out.Q301 = { amount: t.revenue_amount, currency: 'MYR' };
    if (t.overseas_revenue === 'no')
        out['UW-REVENUE-OVERSEAS-PCT'] = 0;
    if (t.activities?.length)
        out['UW-SPECIAL-ACTIVITIES'] = t.activities;
    if (t.card_payments)
        out['UW-PCI'] = t.card_payments;
    return out;
}
/** Eligibility from the intake form alone, before any answers. */
export const intakeEligibilityInput = (t) => ({
    answers: intakeFacts(t),
    controls: {},
    context: { industry: t.industry ?? null },
    ...(t.revenue_amount === undefined && t.revenue ? { revenue_range: REVENUE_RANGE[t.revenue] } : {}),
});
/** The insurer's printed premiums for this revenue, or null without a table or outside its bands. */
export function indicativeQuote(form, revenue) {
    const r = form.rating;
    if (!r || revenue === null || revenue < 0)
        return null;
    const i = r.bands.findIndex((b) => revenue <= b);
    if (i < 0)
        return null;
    return {
        band: { from: i === 0 ? 0 : r.bands[i - 1] + 1, to: r.bands[i] },
        rows: r.rows.map((row) => ({ limit: row.limit, deductible: row.deductible, premium: row.premium[i], with: Object.fromEntries(Object.entries(row.with).map(([k, ps]) => [k, ps[i]])) })), // prettier-ignore
        extensions: r.extensions,
        note: r.note,
    };
}
export const READINESS_STATUSES = ['ready', 'answers_needed', 'referral', 'blocked', 'not_eligible'];
/** The controls a field reads; for a checklist blocker, only those behind the boxes it lacks. */
function fieldControls(f, only) {
    const controls = new Set();
    const visit = referenceVisitor(controls, new Set());
    if (only)
        f.options.filter((o) => o.test && only.includes(o.value)).forEach((o) => visit(o.test));
    else {
        (Array.isArray(f.source) ? f.source : [f.source]).forEach(visit);
        f.options.forEach((o) => o.test && visit(o.test));
    }
    return [...controls].sort();
}
/** Whether a form can go to its insurer: what is missing, what blocks a quote, and the printed premium. */
export function formReadiness(form, filled, eligibility, answers) {
    const defs = new Map(form.sections.flatMap((s) => s.fields.map((f) => [f.key, f])));
    const blockers = [];
    const missing = [];
    for (const f of filled.fields) {
        const def = defs.get(f.key);
        if (f.required && f.status === 'missing')
            missing.push({ key: f.key, ref: f.ref, label: f.label, ...(f.unknown ? { unknown: f.unknown } : {}) }); // prettier-ignore
        const b = def.blocks;
        if (!b || f.status !== 'filled')
            continue;
        const vs = Array.isArray(f.value) ? f.value : [f.value];
        const hit = b.in
            ? vs.some((v) => b.in.includes(v))
            : !vs.some((v) => b.lacks.includes(v));
        if (hit)
            blockers.push({ key: f.key, ref: f.ref, label: f.label, value: f.value, effect: b.effect, note: b.note, controls: fieldControls(def, b.lacks) }); // prettier-ignore
    }
    const status = eligibility.status === 'ineligible'
        ? 'not_eligible'
        : blockers.some((b) => b.effect === 'decline')
            ? 'blocked'
            : blockers.some((b) => b.effect === 'refer')
                ? 'referral'
                : missing.length
                    ? 'answers_needed'
                    : 'ready';
    return { status, completeness: filled.summary.completeness, missing, blockers, quote: indicativeQuote(form, answeredRevenue(answers)?.rm ?? null) }; // prettier-ignore
}
const money = (n) => `RM${n.toLocaleString('en-US')}`;
const cell = (v) => (typeof v === 'boolean' ? (v ? 'Yes' : 'No') : typeof v === 'number' ? v.toLocaleString('en-US') : v == null ? '' : String(v)); // prettier-ignore
/** A value as a key into a target's boxes: a currency answer by its amount. */
const keyOf = (v) => String(v && typeof v === 'object' && 'amount' in v ? v.amount : v);
/** A filled value as the paper form would show it. */
export function formatFormValue(f, v) {
    const label = (x) => f.options.find((o) => o.value === x)?.label ?? String(x);
    if (v === null || v === undefined)
        return '';
    if (Array.isArray(v))
        return v.some((x) => x && typeof x === 'object')
            ? v
                .map((row) => Object.values(row)
                .map(cell)
                .filter(Boolean)
                .join(', '))
                .join('; ')
            : v.map(label).join(', ');
    if (typeof v === 'object') {
        const o = v;
        if (typeof o.amount === 'number')
            return money(o.amount);
        return Object.values(o)
            .filter((x) => x !== null && x !== '')
            .join(', ');
    }
    if (f.type === 'date' && typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v))
        return v.split('-').reverse().join('/');
    if (typeof v === 'number')
        return f.type === 'percentage' ? `${v}%` : f.type === 'currency' ? money(v) : v.toLocaleString('en-US');
    return f.options.length ? label(v) : String(v);
}
/** Splits text over boxes, whole words to a line; the last box takes what's left. */
function splitLines(text, boxes, width = 95) {
    const out = [];
    let rest = text.trim();
    while (rest && out.length < boxes - 1) {
        if (rest.length <= width)
            break;
        const cut = rest.lastIndexOf(' ', width);
        const at = cut > 0 ? cut : width;
        out.push(rest.slice(0, at));
        rest = rest.slice(at).trim();
    }
    return [...out, rest].filter(Boolean);
}
/**
 * The values for the insurer's PDF. Only filled fields are written: a missing answer, and what the director
 * or broker fills, stay blank on the form, as does a checklist until every box is known (a blank box reads
 * as "no").
 */
export function pdfValues(form, filled) {
    const defs = new Map(form.sections.flatMap((s) => s.fields.map((f) => [f.key, f])));
    const out = [];
    for (const f of filled.fields) {
        const t = form.pdf?.fields[f.key];
        if (!t || f.status !== 'filled')
            continue;
        const def = defs.get(f.key);
        if (typeof t === 'string')
            out.push({ widget: t, kind: 'text', value: formatFormValue(def, f.value) });
        else if ('lines' in t)
            splitLines(formatFormValue(def, f.value), t.lines.length).forEach((value, i) => out.push({ widget: t.lines[i], kind: 'text', value })); // prettier-ignore
        else if ('radio' in t) {
            const state = t.states[String(f.value)];
            if (state)
                out.push({ widget: t.radio, kind: 'radio', value: state });
        }
        else if ('boxes' in t) {
            if (t.tick)
                out.push({ widget: t.tick, kind: 'check', value: 'on' });
            for (const v of Array.isArray(f.value) ? f.value : [f.value]) {
                const box = t.boxes[keyOf(v)];
                if (box)
                    out.push({ widget: box, kind: 'check', value: 'on' });
                else if (t.other)
                    out.push({ widget: t.other, kind: 'text', value: formatFormValue(def, v) });
            }
        }
        else {
            const rows = (Array.isArray(f.value) ? f.value : []);
            if ('columns' in t)
                for (const [col, widget] of Object.entries(t.columns)) {
                    const value = rows
                        .map((r) => cell(r[col]))
                        .filter(Boolean)
                        .join('; ');
                    if (value)
                        out.push({ widget, kind: 'text', value });
                }
            else
                rows.forEach((r, i) => {
                    for (const [col, widget] of Object.entries(t.table[t.by ? String(r[t.by]) : String(i)] ?? {}))
                        if (col === '_tick')
                            out.push({ widget, kind: 'check', value: 'on' });
                        else if (cell(r[col]))
                            out.push({ widget, kind: 'text', value: cell(r[col]) });
                });
        }
    }
    return out;
}
/** The box for a chosen limit in the premium grid, when the form prints one (QBE). */
export function pdfQuoteValue(form, revenue, choice) {
    const grid = form.pdf?.quote;
    const quote = indicativeQuote(form, revenue);
    if (!grid || !quote || !form.rating)
        return null;
    const band = form.rating.bands.indexOf(quote.band.to);
    const state = grid.states[`${choice.extension ?? 'main'}:${choice.limit}:${band}`];
    return state ? { widget: grid.radio, kind: 'radio', value: state } : null;
}
// ---------- the submission pack: the filled form and the broker's annex ----------
/** Pages added after the insurer's form: headings, paragraphs and at most one table per section. */
export const PdfAnnex = z.strictObject({
    title: z.string().min(1).max(200),
    subtitle: z.string().max(300).optional(),
    sections: z
        .array(z.strictObject({
        heading: z.string().min(1).max(200),
        paragraphs: z.array(z.string().max(3000)).max(50).default([]),
        table: z
            .strictObject({
            columns: z.array(z.string().min(1).max(100)).min(1).max(6),
            rows: z.array(z.array(z.string().max(3000)).max(6)).max(1000),
        })
            .optional(),
    }))
        .max(20),
});
const PROVENANCE_WORDS = {
    USER_DECLARED: 'Declared by the client',
    AI_EXTRACTED: 'Read from the client’s document, confirmed by the client',
    DOCUMENT_VERIFIED: 'Verified against a document',
    API_DISCOVERED: 'Read from the client’s systems',
    SECURITY_SCAN: 'Found by an internet scan',
    ADMIN_ENTERED: 'Entered by the broker',
    EXTERNAL_PROVIDER: 'From an outside data provider',
    INFERRED: 'Carried over from the client’s submission',
};
const READINESS_WORDS = {
    ready: 'The form is ready to send.',
    answers_needed: 'Some answers are still needed before the form can go.',
    referral: 'An answer refers the case to the insurer’s underwriters.',
    blocked: 'An answer stops a quote.',
    not_eligible: 'The client is outside this form: another form, or the insurer’s underwriters.',
};
const BLOCK_WORDS = {
    refer: 'Referred to underwriters',
    decline: 'No quote',
    terms: 'Narrower terms likely',
};
const day = (iso) => iso.slice(0, 10).split('-').reverse().join('/');
const pct = (n) => `${Math.round(n * 100)}%`;
/** Where a filled field's value came from, in words, with the evidence behind it. */
function sourceOf(f, input) {
    if (f.basis?.startsWith('context:'))
        return 'The client’s details on file';
    if (f.basis === 'checklist')
        return 'Worked out from the client’s Cyber Check answers';
    // `answer:Q301`, `control:IA-01+IA-03`: the questions behind the value.
    const questions = f.basis?.match(/^(?:answer|control):(.+)$/)?.[1].split('+') ?? [];
    const known = questions.flatMap((q) => {
        const a = input.answers[q];
        return a ? [[PROVENANCE_WORDS[a.provenance], ...a.evidence].join(': ')] : [];
    });
    return known.length ? [...new Set(known)].join('; ') : 'The client’s Cyber Check answers';
}
/**
 * The broker's annex to a proposal form (task 92, decision 0069): the client's CyberScore, whether the form
 * is ready, what blocks or changes a quote, what is still to ask, and where each answer on the form came
 * from. It goes after the insurer's own pages and is not part of the insurer's form.
 */
export function submissionAnnex(form, filled, readiness, input) {
    const score = input.score
        ? `CyberScore ${input.score.value} (${input.score.band.replace('_', ' ').toLowerCase()}), scored on ${day(input.score.at)}${input.score.provisional ? ', provisional while required questions are unanswered' : ''}.` // prettier-ignore
        : 'The client has not been scored yet.';
    const sections = [
        {
            heading: 'Summary',
            paragraphs: [
                `Client: ${input.client}. Prepared by ${input.broker} on ${day(input.prepared)}.`,
                score,
                `${READINESS_WORDS[readiness.status]} ${pct(readiness.completeness)} of the required answers are on the form.`,
            ],
        },
    ];
    const q = readiness.quote;
    if (q)
        sections.push({
            heading: 'Indicative premium',
            paragraphs: [`From ${form.insurer}’s printed table for revenue up to ${money(q.band.to)}. ${q.note}`],
            table: {
                columns: ['Limit', 'Deductible', 'Premium', ...Object.values(q.extensions).map((e) => `With ${e.toLowerCase()}`)], // prettier-ignore
                rows: q.rows.map((r) => [money(r.limit), money(r.deductible), money(r.premium), ...Object.keys(q.extensions).map((e) => (r.with[e] === undefined ? '' : money(r.with[e])))]), // prettier-ignore
            },
        });
    if (readiness.blockers.length)
        sections.push({
            heading: 'Answers that change the quote',
            table: { columns: ['Ref', 'Question', 'Effect', 'Note'], rows: readiness.blockers.map((b) => [b.ref, b.label, BLOCK_WORDS[b.effect], b.note]) }, // prettier-ignore
        });
    if (readiness.missing.length)
        sections.push({
            heading: 'Still to answer',
            table: { columns: ['Ref', 'Question'], rows: readiness.missing.map((m) => [m.ref, m.label]) },
        });
    const defs = new Map(form.sections.flatMap((s) => s.fields.map((f) => [f.key, f])));
    sections.push({
        heading: 'Where each answer came from',
        table: {
            columns: ['Ref', 'Question', 'Answer', 'Source'],
            rows: filled.fields.filter((f) => f.status === 'filled').map((f) => [f.ref, f.label, formatFormValue(defs.get(f.key), f.value), sourceOf(f, input)]), // prettier-ignore
        },
    }, {
        heading: 'Declaration',
        paragraphs: [
            `The declaration on ${form.insurer}’s form is for a director or officer of ${input.client} to read and sign. ${input.broker} prepared this annex from the client’s CyberScore assessment. It is not part of the insurer’s form and does not change the client’s duty of disclosure.`, // prettier-ignore
        ],
    });
    return {
        title: `${form.insurer}: ${form.product}`,
        subtitle: `Broker’s annex for ${input.client}`,
        sections,
    };
}
/** The agent service fills the PDF (POST /v1/forms/fill), adding the annex when there is one. */
export const PdfFillRequest = z.strictObject({
    template_base64: z.string().min(4).max(35_000_000),
    sha256: z.string().regex(/^[0-9a-f]{64}$/),
    values: z
        .array(z.strictObject({
        widget: Widget,
        kind: z.enum(['text', 'radio', 'check']),
        value: z.string().max(3000),
    }))
        .max(3000),
    annex: PdfAnnex.optional(),
});
export const PdfFillResult = z.object({
    content_base64: z.string(),
    filled: z.number().int(),
    unknown_widgets: z.array(z.string()),
});
//# sourceMappingURL=insurer-forms.js.map